import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  Send,
  Bot,
  LogOut,
  ThumbsUp,
  ThumbsDown,
  Flag,
  X,
  Plus,
  MessageSquare,
  Phone,
  Pencil,
} from 'lucide-react';
import { Event, User } from '../../types';
import { eventsApi, apiService, feedbackApi, callbackRequestsApi } from '../../services/api';
import { chatSSEService } from '../../services/chatSSEService';
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll';

// ── Helpers ────────────────────────────────────────────────────────────────────

const isApiKeyErrorMsg = (msg?: string) =>
  !!msg && (
    msg.includes('API key') ||
    msg.includes('companion setup') ||
    msg.includes('Knowledge Assistant is currently unavailable') ||
    msg.includes('temporarily unavailable')
  );

// ── Types ──────────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  metadata?: {
    isRAG?: boolean;
    chunksUsed?: number;
  };
}

interface SessionSummary {
  sessionId: string;
  createdAt: string;
  messageCount?: number;
  title?: string;
}

// ── Spinner ────────────────────────────────────────────────────────────────────

const Spinner: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
  const sz = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
  return (
    <svg className={`animate-spin ${sz} text-blue-600`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  );
};

// ── Markdown components (same as EventLandingPage) ─────────────────────────────

const markdownComponents = {
  h1: ({ children }: any) => <h1 style={{ fontSize: '18px', marginBottom: '8px', color: '#1890ff' }}>{children}</h1>,
  h2: ({ children }: any) => <h2 style={{ fontSize: '16px', marginBottom: '6px', color: '#1890ff' }}>{children}</h2>,
  h3: ({ children }: any) => <h3 style={{ fontSize: '14px', marginBottom: '4px', color: '#1890ff' }}>{children}</h3>,
  p:  ({ children }: any) => <p style={{ marginBottom: '8px', lineHeight: '1.5' }}>{children}</p>,
  ul: ({ children }: any) => <ul style={{ marginBottom: '8px', paddingLeft: '20px' }}>{children}</ul>,
  ol: ({ children }: any) => <ol style={{ marginBottom: '8px', paddingLeft: '20px' }}>{children}</ol>,
  li: ({ children }: any) => <li style={{ marginBottom: '4px' }}>{children}</li>,
  strong: ({ children }: any) => <strong style={{ fontWeight: 'bold', color: '#1890ff' }}>{children}</strong>,
  em: ({ children }: any) => <em style={{ fontStyle: 'italic', color: '#722ed1' }}>{children}</em>,
  code: ({ children }: any) => (
    <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' }}>
      {children}
    </code>
  ),
  blockquote: ({ children }: any) => (
    <blockquote style={{ borderLeft: '4px solid #1890ff', paddingLeft: '12px', margin: '8px 0', fontStyle: 'italic' }}>
      {children}
    </blockquote>
  ),
  table: ({ children }: any) => <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>{children}</table>,
  th: ({ children }: any) => <th style={{ border: '1px solid #d9d9d9', padding: '8px', backgroundColor: '#fafafa', fontWeight: 'bold' }}>{children}</th>,
  td: ({ children }: any) => <td style={{ border: '1px solid #d9d9d9', padding: '8px' }}>{children}</td>,
};

// ── Component ──────────────────────────────────────────────────────────────────

const EventChatPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Resolve user from router state or sessionStorage (survives refresh)
  const resolveInitialUser = (): User | null => {
    if ((location.state as any)?.user) return (location.state as any).user as User;
    try {
      const stored = sessionStorage.getItem(`event_user_${eventId}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const [user] = useState<User | null>(resolveInitialUser);
  const [localUser, setLocalUser] = useState<User | null>(resolveInitialUser);
  const [event, setEvent] = useState<Event | null>(null);
  const [eventLoading, setEventLoading] = useState(true);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [submittingFeedbackFor, setSubmittingFeedbackFor] = useState<string | null>(null);

  // Session list
  const [userSessions, setUserSessions] = useState<SessionSummary[]>([]);

  // Modals
  const [callbackModalVisible, setCallbackModalVisible] = useState(false);
  const [callbackValues, setCallbackValues] = useState<Record<string, any>>({});
  const [callbackErrors, setCallbackErrors] = useState<Record<string, string>>({});
  const [reportModal, setReportModal] = useState<{ open: boolean; messageId: string } | null>(null);
  const [reportValues, setReportValues] = useState<Record<string, any>>({});
  const [reportErrors, setReportErrors] = useState<Record<string, string>>({});
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [profileValues, setProfileValues] = useState<Record<string, any>>({});
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [apiKeysNotConfigured, setApiKeysNotConfigured] = useState(false);

  // Refs for duplicate prevention
  const isProcessingRef = useRef(false);
  const activeStreamRef = useRef<string | null>(null);
  const chatInitializedRef = useRef(false);
  const { containerRef: messagesContainerRef, handleScroll: handleMessagesScroll, enableAutoScroll } =
    useChatAutoScroll(messages);

  const useRAG = true;
  const ragTopK = event?.ragTopK ?? 2;

  // ── Shared styles ────────────────────────────────────────────────────────────
  const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const FieldError: React.FC<{ msg?: string }> = ({ msg }) =>
    msg ? <p className="text-xs text-red-500 mt-1">{msg}</p> : null;

  // ── Redirect if not logged in ─────────────────────────────────────────────
  useEffect(() => {
    if (!user) {
      navigate(`/event/${eventId}`, { replace: true });
    }
  }, [user, eventId, navigate]);

  // ── Fetch event ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!eventId) return;
    setEventLoading(true);
    eventsApi.getById(eventId)
      .then((res) => {
        const e = (res.data as any)?.data || (res.data as any);
        setEvent(e || null);
      })
      .catch(() => setEvent(null))
      .finally(() => setEventLoading(false));
  }, [eventId]);

  // ── Load user sessions ────────────────────────────────────────────────────
  const loadUserSessions = useCallback(async (userId: string) => {
    try {
      const res = await apiService.get(`/users/${userId}/sessions?limit=10`);
      if (res.success && res.data) {
        const sessions = Array.isArray(res.data) ? res.data : (res.data as any).sessions || [];
        setUserSessions(sessions);
      }
    } catch {
      // ignore
    }
  }, []);

  const updateSessionTitle = useCallback((targetSessionId: string, title: string) => {
    setUserSessions((prev) =>
      prev.map((s) => s.sessionId === targetSessionId ? { ...s, title } : s)
    );
  }, []);

  // ── startChat ─────────────────────────────────────────────────────────────
  const startChat = useCallback(async () => {
    if (!user || !event) return;
    if (isProcessingRef.current || chatInitializedRef.current) return;

    enableAutoScroll();
    isProcessingRef.current = true;
    chatInitializedRef.current = true;
    setChatLoading(true);

    try {
      const chatRequest = {
        userId: user.userId,
        eventId: event._id,
        message: 'start',
        sessionId: undefined,
        useRAG,
        ragTopK,
      };

      const streamSessionId = `start_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      activeStreamRef.current = streamSessionId;

      const placeholderId = `placeholder_${streamSessionId}`;
      let realConversationId: string | null = null;

      setMessages([{ id: placeholderId, type: 'bot', content: '', timestamp: new Date() }]);

      chatSSEService.connect(streamSessionId, {
        onConnect: () => {
          if (activeStreamRef.current !== streamSessionId) return;
          apiService.post('/chat/stream', { ...chatRequest, streamSessionId }).catch(() => {
            isProcessingRef.current = false;
            setChatLoading(false);
            setMessages([{ id: Date.now().toString(), type: 'bot', content: 'Failed to start chat. Please try again.', timestamp: new Date() }]);
            chatSSEService.disconnect(`chat_${streamSessionId}`);
          });
        },
        onStart: (convId: string) => {
          if (activeStreamRef.current !== streamSessionId) return;
          realConversationId = convId;
          setMessages((prev) => prev.map((m) => m.id === placeholderId ? { ...m, id: convId } : m));
        },
        onToken: (text: string, convId: string) => {
          if (activeStreamRef.current !== streamSessionId) return;
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === convId || m.id === realConversationId || m.id === placeholderId || m.id.startsWith('placeholder_'));
            if (idx !== -1) {
              return prev.map((m, i) => i === idx ? { ...m, id: convId || m.id, content: m.content + text } : m);
            }
            return prev;
          });
        },
        onReset: (convId: string) => {
          // The primary model failed partway through; another one is re-answering.
          // Clear the partial text so the replacement does not append to a broken sentence.
          if (activeStreamRef.current !== streamSessionId) return;
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === convId || m.id === realConversationId || m.id === placeholderId || m.id.startsWith('placeholder_'));
            if (idx !== -1) {
              return prev.map((m, i) => i === idx ? { ...m, content: '' } : m);
            }
            return prev;
          });
        },
        onRAGInfo: (chunks: number) => {
          if (activeStreamRef.current !== streamSessionId) return;
          setMessages((prev) =>
            prev.map((m) => m.id === realConversationId || m.id === placeholderId
              ? { ...m, metadata: { ...m.metadata, isRAG: true, chunksUsed: chunks } }
              : m
            )
          );
        },
        onComplete: (finalSessionId: string, _convId: string, sessionTitle?: string) => {
          if (finalSessionId && !sessionId) setSessionId(finalSessionId);
          if (sessionTitle && finalSessionId) updateSessionTitle(finalSessionId, sessionTitle);
          if (user) loadUserSessions(user.userId);
          setChatLoading(false);
          isProcessingRef.current = false;
        },
        onSessionTitle: (sid: string, title: string) => {
          updateSessionTitle(sid, title);
        },
        onError: (errorMsg: string) => {
          isProcessingRef.current = false;
          setChatLoading(false);
          if (isApiKeyErrorMsg(errorMsg)) setApiKeysNotConfigured(true);
          const fallback = errorMsg || 'Sorry, something went wrong.';
          setMessages((prev) => {
            if (prev.length === 0) {
              return [{ id: Date.now().toString(), type: 'bot', content: fallback, timestamp: new Date() }];
            }
            const hasEmptyPlaceholder = prev.some((m) => m.type === 'bot' && !m.content);
            if (hasEmptyPlaceholder) {
              return prev.map((m) =>
                m.type === 'bot' && !m.content ? { ...m, content: fallback } : m
              );
            }
            return prev;
          });
          chatSSEService.disconnect(`chat_${streamSessionId}`);
        },
      });
    } catch (error: any) {
      isProcessingRef.current = false;
      setChatLoading(false);
      const msg = error?.response?.data?.message || error?.message;
      if (isApiKeyErrorMsg(msg)) setApiKeysNotConfigured(true);
      setMessages([{
        id: Date.now().toString(),
        type: 'bot',
        content: isApiKeyErrorMsg(msg)
          ? msg
          : `Welcome to ${event!.name}! I'm your AI assistant. What would you like to know?`,
        timestamp: new Date(),
      }]);
    }
  }, [user, event, useRAG, ragTopK, sessionId, loadUserSessions, enableAutoScroll]);

  // ── Auto-start chat on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (event && user && !chatInitializedRef.current) {
      startChat();
    }
  }, [event, user, startChat]);

  // ── Detect API key errors from any message path ───────────────────────────
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.type === 'bot' && isApiKeyErrorMsg(last.content)) {
      setApiKeysNotConfigured(true);
    }
  }, [messages]);

  // ── sendMessage ───────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!inputMessage.trim() || !event || !user) return;
    if (isProcessingRef.current) return;

    enableAutoScroll();
    isProcessingRef.current = true;

    const userMsg: ChatMessage = { id: Date.now().toString(), type: 'user', content: inputMessage, timestamp: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setChatLoading(true);

    try {
      const chatRequest = {
        userId: user.userId,
        eventId: event._id,
        message: currentMessage,
        sessionId: sessionId || undefined,
        useRAG,
        ragTopK,
      };

      const streamSessionId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      activeStreamRef.current = streamSessionId;

      const placeholderId = `placeholder_${streamSessionId}`;
      let realConversationId: string | null = null;

      setMessages((prev) => [...prev, { id: placeholderId, type: 'bot', content: '', timestamp: new Date() }]);

      chatSSEService.connect(streamSessionId, {
        onConnect: () => {
          if (activeStreamRef.current !== streamSessionId) return;
          apiService.post('/chat/stream', { ...chatRequest, streamSessionId }).catch((err) => {
            isProcessingRef.current = false;
            setChatLoading(false);
            const errMsg = err?.response?.data?.message || 'Sorry, something went wrong.';
            if (isApiKeyErrorMsg(errMsg)) setApiKeysNotConfigured(true);
            setMessages((prev) => [...prev.filter((m) => m.id !== placeholderId), { id: Date.now().toString(), type: 'bot', content: errMsg, timestamp: new Date() }]);
            chatSSEService.disconnect(`chat_${streamSessionId}`);
          });
        },
        onStart: (convId: string) => {
          if (activeStreamRef.current !== streamSessionId) return;
          realConversationId = convId;
          setMessages((prev) => prev.map((m) => m.id === placeholderId ? { ...m, id: convId } : m));
        },
        onToken: (text: string, convId: string) => {
          if (activeStreamRef.current !== streamSessionId) return;
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === convId || m.id === realConversationId || m.id === placeholderId || m.id.startsWith('placeholder_'));
            if (idx !== -1) {
              return prev.map((m, i) => i === idx ? { ...m, id: convId || m.id, content: m.content + text } : m);
            }
            return prev;
          });
        },
        onReset: (convId: string) => {
          // The primary model failed partway through; another one is re-answering.
          // Clear the partial text so the replacement does not append to a broken sentence.
          if (activeStreamRef.current !== streamSessionId) return;
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === convId || m.id === realConversationId || m.id === placeholderId || m.id.startsWith('placeholder_'));
            if (idx !== -1) {
              return prev.map((m, i) => i === idx ? { ...m, content: '' } : m);
            }
            return prev;
          });
        },
        onRAGInfo: (chunks: number) => {
          if (activeStreamRef.current !== streamSessionId) return;
          setMessages((prev) =>
            prev.map((m) => m.id === realConversationId || m.id.startsWith('placeholder_')
              ? { ...m, metadata: { ...m.metadata, isRAG: true, chunksUsed: chunks } }
              : m
            )
          );
        },
        onComplete: (finalSessionId: string) => {
          if (finalSessionId && !sessionId) setSessionId(finalSessionId);
          if (user) loadUserSessions(user.userId);
          setChatLoading(false);
          isProcessingRef.current = false;
        },
        onSessionTitle: (sid: string, title: string) => {
          updateSessionTitle(sid, title);
        },
        onError: (errorMsg: string) => {
          if (isApiKeyErrorMsg(errorMsg)) setApiKeysNotConfigured(true);
          setMessages((prev) => [...prev, { id: Date.now().toString(), type: 'bot', content: errorMsg || 'Sorry, something went wrong.', timestamp: new Date() }]);
          setChatLoading(false);
          isProcessingRef.current = false;
          chatSSEService.disconnect(`chat_${streamSessionId}`);
        },
      });
    } catch (error: any) {
      const errMsg = error?.response?.data?.message || 'Sorry, something went wrong.';
      if (isApiKeyErrorMsg(errMsg)) setApiKeysNotConfigured(true);
      setMessages((prev) => [...prev, { id: Date.now().toString(), type: 'bot', content: errMsg, timestamp: new Date() }]);
      setChatLoading(false);
      isProcessingRef.current = false;
    }
  };

  // ── New session ───────────────────────────────────────────────────────────
  const startNewSession = () => {
    isProcessingRef.current = false;
    chatInitializedRef.current = false;
    activeStreamRef.current = null;
    setChatLoading(false);
    setInputMessage('');
    setMessages([]);
    setSessionId(null);
    setTimeout(() => startChat(), 0);
  };

  // ── Load session history ──────────────────────────────────────────────────
  const loadSessionHistory = async (sid: string) => {
    try {
      const res = await apiService.get(`/chat/history/${sid}`);
      const convos: any[] = (res as any)?.data?.conversations || [];
      const msgs: ChatMessage[] = [];
      convos.forEach((c) => {
        msgs.push({ id: `u_${c.messageId}`, type: 'user', content: c.userMessage, timestamp: new Date(c.timestamp) });
        msgs.push({ id: c.messageId, type: 'bot', content: c.botResponse, timestamp: new Date(c.timestamp) });
      });
      enableAutoScroll();
      setMessages(msgs);
      setSessionId(sid);
      chatInitializedRef.current = true;
    } catch {
      toast.error('Could not load session history.');
    }
  };

  // ── Feedback ──────────────────────────────────────────────────────────────
  const handleQuickFeedback = async (messageId: string, rating: number, summary: string) => {
    if (!event || submittingFeedbackFor) return;
    try {
      setSubmittingFeedbackFor(messageId);
      await feedbackApi.create({ sessionId: sessionId || undefined, messageId, eventId: event._id, rating, summary, category: 'other' });
      toast.success('Thanks for the feedback!');
    } catch {
      toast.error('Could not submit feedback. Please try again.');
    } finally {
      setSubmittingFeedbackFor(null);
    }
  };

  const submitReport = async () => {
    const errors: Record<string, string> = {};
    if (reportValues.rating === undefined || reportValues.rating === null || reportValues.rating === '') errors.rating = 'Required';
    if (!reportValues.category) errors.category = 'Required';
    if (!reportValues.summary || !String(reportValues.summary).trim()) errors.summary = 'Required';
    if (Object.keys(errors).length) { setReportErrors(errors); return; }
    setReportErrors({});
    try {
      if (!event) return;
      await feedbackApi.create({ sessionId: sessionId || undefined, messageId: reportModal?.messageId, eventId: event._id, rating: Number(reportValues.rating), category: reportValues.category, summary: reportValues.summary, details: reportValues.details });
      setReportModal(null);
      setReportValues({});
      toast.success('Thanks for the feedback!');
    } catch {
      toast.error('Could not submit feedback. Please try again.');
    }
  };

  // ── Callback ──────────────────────────────────────────────────────────────
  const submitCallbackRequest = async () => {
    const errors: Record<string, string> = {};
    if (!callbackValues.issue || !String(callbackValues.issue).trim()) errors.issue = 'Please describe your issue';
    if (!callbackValues.expectedResolution || !String(callbackValues.expectedResolution).trim()) errors.expectedResolution = 'Please describe the expected resolution';
    if (Object.keys(errors).length) { setCallbackErrors(errors); return; }
    setCallbackErrors({});
    if (!event || !user) return;
    const phone = (user as any).phone;
    if (!phone || !String(phone).trim()) { toast.error('Phone number is missing.'); return; }
    try {
      await callbackRequestsApi.create({ eventId: event._id, userId: user.userId, sessionId: sessionId || undefined, phone: String(phone).trim(), issue: callbackValues.issue, expectedResolution: callbackValues.expectedResolution });
      toast.success('Callback request submitted. We will contact you shortly.');
      setCallbackModalVisible(false);
      setCallbackValues({});
    } catch (e: any) {
      if (e?.response?.data?.error) toast.error(e.response.data.error);
    }
  };

  // ── Profile update ────────────────────────────────────────────────────────
  const handleUpdateProfile = () => {
    if (!localUser) return;
    setProfileValues({
      name: localUser.name,
      email: localUser.email,
      occupation: localUser.occupation,
      businessNature: localUser.businessNature,
      businessDetails: localUser.businessDetails,
    });
    setProfileModalVisible(true);
  };

  const handleProfileSave = async () => {
    const errors: Record<string, string> = {};
    if (!profileValues.name?.trim()) errors.name = 'Please enter your name';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!profileValues.email || !emailRegex.test(profileValues.email)) errors.email = 'Please enter a valid email';
    if (Object.keys(errors).length) { setProfileErrors(errors); return; }
    setProfileErrors({});
    if (!localUser) return;
    try {
      const res = await apiService.put(`/users/${localUser.userId}`, {
        name: profileValues.name,
        email: profileValues.email,
        occupation: profileValues.occupation || '',
        businessNature: profileValues.businessNature || '',
        businessDetails: profileValues.businessDetails || '',
      });
      if (res.success && res.data) {
        const updated = (res as any).data;
        setLocalUser(updated);
        sessionStorage.setItem(`event_user_${eventId}`, JSON.stringify(updated));
        setProfileModalVisible(false);
        setProfileValues({});
        toast.success('Profile updated!');
      } else {
        toast.error('Failed to update profile. Please try again.');
      }
    } catch {
      toast.error('Failed to update profile. Please try again.');
    }
  };

  // ── Logout ────────────────────────────────────────────────────────────────
  const handleLogout = () => {
    sessionStorage.removeItem(`event_user_${eventId}`);
    navigate(`/event/${eventId}`, { replace: true });
  };

  // ── Avatar helper ─────────────────────────────────────────────────────────
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
  };

  // ── Loading / redirect ────────────────────────────────────────────────────
  if (!user) return null;

  if (eventLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <h2 className="text-2xl font-semibold text-gray-700">Knowledge Assistant not found</h2>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <div className="w-72 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">

        {/* Event branding */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Bot size={16} className="text-blue-600" />
            </div>
            <span className="font-semibold text-gray-800 text-sm leading-tight line-clamp-2">{event.name}</span>
          </div>
        </div>

        {/* User profile */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {getInitials(localUser!.name)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 truncate">{localUser!.name}</p>
              <p className="text-xs text-gray-500 truncate">{localUser!.email}</p>
            </div>
            <button
              type="button"
              onClick={handleUpdateProfile}
              title="Edit profile"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <Pencil size={14} />
            </button>
          </div>
        </div>

        {/* Session history */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Recent Sessions</p>
            {userSessions.length === 0 ? (
              <p className="text-xs text-gray-400 px-1">No previous sessions</p>
            ) : (
              <ul className="space-y-1">
                {userSessions.map((s) => (
                  <li key={s.sessionId}>
                    <button
                      type="button"
                      onClick={() => loadSessionHistory(s.sessionId)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                        sessionId === s.sessionId
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare size={12} className="flex-shrink-0 opacity-60" />
                        <span className="truncate">
                          {s.title || new Date(s.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {s.title && (
                        <p className="text-gray-400 mt-0.5 pl-4">
                          {new Date(s.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Logout */}
        <div className="px-4 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut size={15} />
            Sign out
          </button>
        </div>
      </div>

      {/* ── Main chat area ────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3 flex-shrink-0">
          <Bot size={18} className="text-blue-600 flex-shrink-0" />
          <span className="font-semibold text-gray-800 flex-1 truncate">{event.name}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={startNewSession}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
            >
              <Plus size={14} />
              New session
            </button>
            <button
              type="button"
              onClick={() => {
                isProcessingRef.current = false;
                chatInitializedRef.current = false;
                activeStreamRef.current = null;
                setChatLoading(false);
                setInputMessage('');
                setMessages([]);
                setSessionId(null);
                setTimeout(() => startChat(), 0);
              }}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Clear chat
            </button>
            <button
              type="button"
              onClick={() => setCallbackModalVisible(true)}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Phone size={13} />
              Request a callback
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="flex-1 overflow-y-auto p-6 bg-gray-50"
        >
          <AnimatePresence>
            {messages.map((msg) => (
              <motion.div
                key={msg.id}
                className={`mb-4 flex ${msg.type === 'bot' ? 'justify-start' : 'justify-end'}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className={`max-w-[70%] px-4 py-3 rounded-2xl shadow-sm break-words md:max-w-[85%] ${
                    msg.type === 'bot' ? 'bg-gray-100 text-gray-900' : 'bg-blue-500 text-white'
                  }`}
                >
                  {msg.type === 'bot' ? (
                    msg.content ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {msg.content}
                      </ReactMarkdown>
                    ) : (
                      <div className="text-gray-500 flex items-center gap-2">
                        <Spinner size="sm" />
                        <span className="italic">Thinking...</span>
                      </div>
                    )
                  ) : (
                    msg.content
                  )}

                  {msg.type === 'bot' && msg.content && (
                    <div className="mt-2 flex gap-1 items-center opacity-80">
                      <button
                        type="button"
                        title="Helpful"
                        onClick={() => handleQuickFeedback(msg.id, 5, 'Thumbs Up')}
                        disabled={submittingFeedbackFor === msg.id}
                        className="p-1 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-50"
                      >
                        <ThumbsUp size={14} />
                      </button>
                      <button
                        type="button"
                        title="Not helpful"
                        onClick={() => handleQuickFeedback(msg.id, 1, 'Thumbs Down')}
                        disabled={submittingFeedbackFor === msg.id}
                        className="p-1 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-50"
                      >
                        <ThumbsDown size={14} />
                      </button>
                      <button
                        type="button"
                        title="Report issue"
                        onClick={() => setReportModal({ open: true, messageId: msg.id })}
                        className="p-1 rounded hover:bg-gray-200 text-gray-500"
                      >
                        <Flag size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {chatLoading && messages.length === 0 && (
            <div className="mb-4 flex justify-start">
              <div className="max-w-[70%] px-4 py-3 rounded-2xl shadow-sm bg-gray-100 text-gray-900">
                <div className="flex items-center gap-2">
                  <Spinner size="sm" />
                  <span>Assistant is thinking...</span>
                </div>
              </div>
            </div>
          )}

          {messages.length === 0 && !chatLoading && (
            <div className="text-center py-16 text-gray-500">
              <Bot size={48} className="mx-auto mb-4 text-gray-300" />
              <div className="text-lg mb-2">Welcome to {event.name}!</div>
              <div className="text-sm">Start a conversation by typing your question below.</div>
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
          <div className="flex gap-2">
            <textarea
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
              }}
              placeholder="Ask a question… (Shift+Enter for new line)"
              disabled={chatLoading || apiKeysNotConfigured}
              rows={3}
            />
            <button
              type="button"
              onClick={sendMessage}
              disabled={!inputMessage.trim() || chatLoading || apiKeysNotConfigured}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg flex items-center gap-1 transition-colors self-end"
            >
              <Send size={16} />
              Send
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400 text-center">
            AI may generate inaccurate information. Please verify important details.
          </p>
        </div>
      </div>

      {/* ── Report Modal ──────────────────────────────────────────────────── */}
      {reportModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="font-semibold text-gray-800">Report an issue</span>
              <button type="button" onClick={() => { setReportModal(null); setReportValues({}); setReportErrors({}); }} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className={labelCls}>Rating (-1 to 5)</label>
                <input type="number" min={-1} max={5} className={inputCls} placeholder="-1 to 5" value={reportValues.rating ?? ''} onChange={(e) => setReportValues((v) => ({ ...v, rating: e.target.value }))} />
                <FieldError msg={reportErrors.rating} />
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <select className={inputCls} value={reportValues.category || ''} onChange={(e) => setReportValues((v) => ({ ...v, category: e.target.value }))}>
                  <option value="">Select category</option>
                  {['accuracy', 'hallucination', 'tone', 'latency', 'ui', 'other'].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <FieldError msg={reportErrors.category} />
              </div>
              <div>
                <label className={labelCls}>Summary</label>
                <input type="text" className={inputCls} placeholder="Short title" value={reportValues.summary || ''} onChange={(e) => setReportValues((v) => ({ ...v, summary: e.target.value }))} />
                <FieldError msg={reportErrors.summary} />
              </div>
              <div>
                <label className={labelCls}>Details</label>
                <textarea className={inputCls} rows={4} placeholder="Describe the issue…" value={reportValues.details || ''} onChange={(e) => setReportValues((v) => ({ ...v, details: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button type="button" onClick={() => { setReportModal(null); setReportValues({}); setReportErrors({}); }} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={submitReport} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Profile Modal ─────────────────────────────────────────────────── */}
      {profileModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 font-semibold text-gray-800">
                <Pencil size={16} />
                Update Profile
              </div>
              <button type="button" onClick={() => { setProfileModalVisible(false); setProfileValues({}); setProfileErrors({}); }} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full Name</label>
                  <input type="text" className={inputCls} placeholder="Enter your full name" value={profileValues.name || ''} onChange={(e) => setProfileValues((v) => ({ ...v, name: e.target.value }))} />
                  <FieldError msg={profileErrors.name} />
                </div>
                <div>
                  <label className={labelCls}>Email Address</label>
                  <input type="email" className={inputCls} placeholder="Enter your email" value={profileValues.email || ''} onChange={(e) => setProfileValues((v) => ({ ...v, email: e.target.value }))} />
                  <FieldError msg={profileErrors.email} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Occupation</label>
                  <input type="text" className={inputCls} placeholder="Your occupation" value={profileValues.occupation || ''} onChange={(e) => setProfileValues((v) => ({ ...v, occupation: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Industry</label>
                  <input type="text" className={inputCls} placeholder="Your industry" value={profileValues.businessNature || ''} onChange={(e) => setProfileValues((v) => ({ ...v, businessNature: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Additional Details</label>
                <textarea className={inputCls} rows={3} placeholder="Any additional information…" value={profileValues.businessDetails || ''} onChange={(e) => setProfileValues((v) => ({ ...v, businessDetails: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button type="button" onClick={() => { setProfileModalVisible(false); setProfileValues({}); setProfileErrors({}); }} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={handleProfileSave} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Callback Modal ────────────────────────────────────────────────── */}
      {callbackModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="font-semibold text-gray-800">Request a callback</span>
              <button type="button" onClick={() => { setCallbackModalVisible(false); setCallbackValues({}); setCallbackErrors({}); }} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className={labelCls}>Issue</label>
                <input type="text" className={inputCls} placeholder="What do you need help with?" value={callbackValues.issue || ''} onChange={(e) => setCallbackValues((v) => ({ ...v, issue: e.target.value }))} />
                <FieldError msg={callbackErrors.issue} />
              </div>
              <div>
                <label className={labelCls}>Expected resolution</label>
                <textarea className={inputCls} rows={3} placeholder="What outcome are you expecting?" value={callbackValues.expectedResolution || ''} onChange={(e) => setCallbackValues((v) => ({ ...v, expectedResolution: e.target.value }))} />
                <FieldError msg={callbackErrors.expectedResolution} />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button type="button" onClick={() => { setCallbackModalVisible(false); setCallbackValues({}); setCallbackErrors({}); }} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="button" onClick={submitCallbackRequest} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventChatPage;
